// src/scenes/Chat/index.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  IconButton,
  Typography,
  Paper,
  Button,
  TextareaAutosize,
  useTheme,
  Divider,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import SendIcon from '@mui/icons-material/Send';

export default function Chat() {
  const theme = useTheme();

  // CSS var fallback: if your app shell sets --app-topbar-height, we’ll respect it.
  // This component no longer hard-positions itself; it lives in the normal flow
  // so it will naturally grow/shrink with the global sidebar open/close.
  const TOPBAR_CSS_VAR = 'var(--app-topbar-height, 64px)';

  // Sessions
  const [sessions, setSessions] = useState([
    { id: '1', name: 'Session 1' },
    { id: '2', name: 'Session 2' },
  ]);
  const [currentSession, setCurrentSession] = useState(sessions[0].id);

  // Messages
  const [messages, setMessages] = useState({ '1': [], '2': [] });
  const [input, setInput] = useState('');

  const fileInputRef = useRef(null);
  const messageContainerRef = useRef(null);

  // Auto-scroll to bottom on new messages/session change
  useEffect(() => {
    const el = messageContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, currentSession]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    const newMsg = { sender: 'user', type: 'text', content: text, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...(prev[currentSession] || []), newMsg],
    }));
    setInput('');
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const newMsg = { sender: 'user', type: 'image', src: url, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...(prev[currentSession] || []), newMsg],
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerateImage = () => {
    const prompt = window.prompt('Describe the image to generate:');
    if (!prompt) return;
    const url = `https://via.placeholder.com/640x360?text=${encodeURIComponent(prompt)}`;
    const newMsg = { sender: 'agent', type: 'image', src: url, timestamp: Date.now() };
    setMessages(prev => ({
      ...prev,
      [currentSession]: [...(prev[currentSession] || []), newMsg],
    }));
  };

  const sessionMessages = messages[currentSession] || [];

  return (
    // This outer wrapper sits under the global Topbar and inside your app’s main content area.
    // It uses flex + overflow control so ONLY the messages area scrolls.
    <Box
      sx={{
        height: `calc(100vh - ${TOPBAR_CSS_VAR})`,
        display: 'flex',
        overflow: 'hidden', // prevent page scroll; let inner panes handle it
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Local Sessions Sidebar (this stays aligned because the whole page
          layout already accounts for the GLOBAL sidebar; we don't pin/fix here) */}
      <Box
        sx={{
          width: 240,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          minWidth: 240,
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" color="text.primary">Chats</Typography>
        </Box>

        <Divider />

        <List sx={{ px: 1, overflowY: 'auto', flexGrow: 1 }}>
          {sessions.map(sess => (
            <ListItem
              key={sess.id}
              button
              selected={sess.id === currentSession}
              onClick={() => setCurrentSession(sess.id)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                color: sess.id === currentSession ? theme.palette.primary.main : theme.palette.text.primary,
              }}
            >
              {sess.name}
            </ListItem>
          ))}
        </List>

        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              const name = window.prompt('New session name:');
              if (!name) return;
              const id = Date.now().toString();
              setSessions(prev => [...prev, { id, name }]);
              setMessages(prev => ({ ...prev, [id]: [] }));
              setCurrentSession(id);
            }}
          >
            New Chat
          </Button>
        </Box>
      </Box>

      {/* Chat Column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Slim header (agent selector removed) */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: theme.palette.background.paper,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            {sessions.find(s => s.id === currentSession)?.name || 'Chat'}
          </Typography>
        </Box>

        {/* Messages (scrolls) */}
        <Box
          ref={messageContainerRef}
          id="message-container"
          sx={{
            flex: 1,
            minHeight: 0,      // critical for flex scrolling
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {sessionMessages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  maxWidth: { xs: '90%', sm: '75%', md: '65%' },
                  borderRadius: 2,
                  bgcolor: theme.palette.background.paper,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {msg.type === 'text' ? (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }} color="text.primary">
                    {msg.content}
                  </Typography>
                ) : (
                  <Box component="img" src={msg.src} alt="chat-img" sx={{ width: '100%', borderRadius: 1 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Input Bar */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            bgcolor: theme.palette.background.paper,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <IconButton onClick={() => fileInputRef.current?.click()}>
            <AddPhotoAlternateIcon />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <IconButton onClick={handleGenerateImage}>
            <ImageSearchIcon />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: theme.palette.background.paper,
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <TextareaAutosize
              minRows={1}
              maxRows={8}
              placeholder="Type a message…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              style={{
                width: '100%',
                resize: 'none',
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: theme.palette.text.primary,
                font: 'inherit',
              }}
            />
          </Box>

          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
